import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import MoneyInput from "@/components/MoneyInput";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Loader2, CreditCard, CheckCircle2, Clock, AlertTriangle, Undo2, ChevronDown } from "lucide-react";
import { CardImportButton } from "@/components/CardImportButton";

const TODAY = new Date().toISOString().slice(0, 10);

function monthLabel(ym) {
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
}

const Badge = ({ className, children }) => (
  <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-black ${className}`}>{children}</span>
);

const InvoiceItems = ({ items }) => (
  <ul className="mt-2 divide-y divide-slate-100 bg-white rounded-lg border border-slate-100">
    {items.length === 0 && <li className="px-3 py-2 text-xs text-slate-500 italic">Sem compras nesta fatura.</li>}
    {items.map((it) => (
      <li key={`${it.expense_id}-${it.installment}`} className="px-3 py-2 flex items-center gap-2 text-xs" data-testid={`card-item-${it.expense_id}-${it.installment}`}>
        <span className="text-slate-500 tabular-nums shrink-0">{formatDateBR(it.date)}</span>
        <span className="flex-1 min-w-0 truncate text-slate-800">{it.description || "Compra no cartão"}</span>
        {it.legacy && <Badge className="bg-amber-100 text-amber-700 shrink-0">EXTRATO</Badge>}
        {it.installments > 1 && <Badge className="bg-indigo-100 text-indigo-700">{it.installment}/{it.installments}</Badge>}
        <span className="font-black tabular-nums text-slate-900">{BRL(it.value)}</span>
      </li>
    ))}
  </ul>
);

const InvoiceRow = ({ inv, onPay, onUndo }) => {
  const [open, setOpen] = useState(!inv.paid && inv.is_open);
  const canPay = TODAY >= inv.due_date;
  const tone = inv.paid ? "border-emerald-200 bg-emerald-50/40" : inv.is_overdue ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-slate-50";
  return (
    <li className={`p-3 rounded-xl border ${tone}`} data-testid={`card-invoice-${inv.month}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 min-w-0 text-left" data-testid={`card-toggle-${inv.month}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-black text-slate-900 capitalize">Fatura de {monthLabel(inv.month)}</p>
            {inv.paid && <Badge className="bg-emerald-600 text-white"><CheckCircle2 className="w-3 h-3" /> PAGA</Badge>}
            {!inv.paid && inv.is_open && <Badge className="bg-sky-100 text-sky-700">ABERTA</Badge>}
            {inv.is_overdue && <Badge className="bg-rose-600 text-white"><AlertTriangle className="w-3 h-3" /> VENCIDA</Badge>}
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
          </div>
          <p className="text-[11px] text-slate-500">
            Fecha {formatDateBR(inv.closing_date)} • Vence {formatDateBR(inv.due_date)} • {inv.items.length} lançamento(s)
            {inv.paid && ` • Paga em ${formatDateBR(inv.paid_date)}`}
          </p>
          {inv.bank_total != null && !inv.paid && (
            <p className="text-[11px] font-semibold text-slate-700 mt-0.5" data-testid={`card-bank-${inv.month}`}>
              No banco: {BRL(inv.bank_total)} • No sistema: {BRL(inv.total)}
              <span className={inv.total - inv.bank_total > 0 ? "text-rose-600" : "text-emerald-700"}> ({inv.total - inv.bank_total > 0 ? "+" : "−"}{BRL(Math.abs(inv.total - inv.bank_total))})</span>
            </p>
          )}
          {inv.legacy_total > 0 && <p className="text-[10.5px] text-amber-700">Inclui {BRL(inv.legacy_total)} de parcelas de antes do sistema (não entram nos orçamentos)</p>}
        </button>
        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <p className={`text-base font-black tabular-nums ${inv.paid ? "text-emerald-700" : "text-indigo-700"}`}>
            {BRL(inv.paid ? inv.paid_amount : inv.total)}
          </p>
          {inv.paid ? (
            <Button variant="ghost" onClick={() => onUndo(inv.month)} data-testid={`card-undo-${inv.month}`} className="h-8 px-2 text-slate-500 hover:text-rose-600">
              <Undo2 className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={() => canPay && onPay(inv)}
              disabled={!canPay || inv.total <= 0}
              title={canPay ? "" : `Disponível a partir de ${formatDateBR(inv.due_date)}`}
              data-testid={`card-pay-${inv.month}`}
              className="h-9 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-xl text-xs font-black"
            >
              {canPay ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <Clock className="w-4 h-4 mr-1" />}
              Pagar fatura
            </Button>
          )}
        </div>
      </div>
      {open && <InvoiceItems items={inv.items} />}
    </li>
  );
};

export const PayDialog = ({ target, onClose, onDone }) => {
  const [date, setDate] = useState(TODAY);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [debit, setDebit] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) { setDate(TODAY); setAmount(String(target.total)); setNote(""); setDebit(false); }
  }, [target]);

  const confirm = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Informe um valor válido"); return; }
    setSaving(true);
    try {
      await api.post(`/finance/cartao/${target.month}/pay`, { paid_date: date, amount: amt, note, debit_caixa: debit });
      toast.success(debit ? "Fatura paga — valor debitado do caixa" : "Fatura marcada como paga");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao pagar fatura");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="card-pay-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-indigo-600" /> Pagar fatura do cartão</DialogTitle>
        </DialogHeader>
        {target && (
          <div className="space-y-4">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
              <p className="text-base font-black text-slate-900 capitalize">Fatura de {monthLabel(target.month)}</p>
              <p className="text-[11px] text-slate-500">Total da fatura: <b className="text-indigo-700">{BRL(target.total)}</b> • Vence {formatDateBR(target.due_date)}</p>
            </div>
            <div>
              <label className="text-[11px] font-black uppercase tracking-widest text-slate-600">Data do pagamento</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="card-pay-date" className="mt-1 h-10 rounded-xl" />
            </div>
            <div>
              <label className="text-[11px] font-black uppercase tracking-widest text-slate-600">Valor pago (R$)</label>
              <MoneyInput value={amount} onChange={setAmount} data-testid="card-pay-amount" className="mt-1 h-10 rounded-xl" />
            </div>
            <div>
              <label className="text-[11px] font-black uppercase tracking-widest text-slate-600">Observação (opcional)</label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="card-pay-note" className="mt-1 rounded-xl text-sm" />
            </div>
            <label className="flex items-start gap-2 cursor-pointer p-3 rounded-xl border border-sky-200 bg-sky-50">
              <Checkbox checked={debit} onCheckedChange={(v) => setDebit(Boolean(v))} data-testid="card-pay-debit-caixa" className="mt-0.5" />
              <span className="text-xs text-sky-900"><b>Debitar do Caixa da empresa</b>
                <span className="block text-[10.5px] text-sky-700">Cria automaticamente uma saída no caixa com o valor da fatura.</span>
              </span>
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancelar</Button>
          <Button onClick={confirm} disabled={saving} data-testid="card-pay-confirm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Confirmar pagamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function FinanceCartao() {
  const [data, setData] = useState(null);
  const [payTarget, setPayTarget] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/finance/cartao");
      setData(r.data);
    } catch {
      toast.error("Falha ao carregar faturas");
    }
  };
  useEffect(() => { load(); }, []);

  const undo = async (month) => {
    if (!window.confirm("Desfazer o pagamento desta fatura? A saída do caixa também será removida.")) return;
    try {
      await api.delete(`/finance/cartao/${month}/pay`);
      toast.success("Pagamento desfeito");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao desfazer");
    }
  };

  if (!data) return (
    <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando…</div>
  );

  const current = data.invoices.find((i) => i.month === data.current_month);

  return (
    <div className="space-y-5 max-w-5xl pb-16" data-testid="finance-cartao-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center"><CreditCard className="w-5 h-5 text-white" /></div>
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-xl sm:text-2xl font-black text-slate-900">Cartão de crédito</h1>
          <p className="text-xs sm:text-sm text-slate-500">Compras de material no cartão. Fecha dia {data.closing_day}, vence dia {data.due_day}.</p>
        </div>
      </div>
      <CardImportButton currentMonth={data.current_month} onDone={load} />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white p-4" data-testid="card-current-total">
          <p className="text-[10px] font-black uppercase tracking-widest opacity-80">Fatura atual</p>
          <p className="mt-2 text-xl sm:text-2xl font-black tabular-nums">{BRL(current?.total || 0)}</p>
          <p className="text-[10px] opacity-80 mt-0.5 capitalize">{monthLabel(data.current_month)}</p>
        </div>
        <div className="rounded-2xl bg-white border border-rose-200 p-4" data-testid="card-pending-total">
          <p className="text-[10px] font-black uppercase tracking-widest text-rose-700">A pagar (total)</p>
          <p className="mt-2 text-xl font-black tabular-nums text-rose-700">{BRL(data.total_pending)}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">inclui parcelas futuras</p>
        </div>
        <div className="rounded-2xl bg-white border border-emerald-200 p-4 col-span-2 sm:col-span-1" data-testid="card-paid-total">
          <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Já pago</p>
          <p className="mt-2 text-xl font-black tabular-nums text-emerald-700">{BRL(data.total_paid)}</p>
        </div>
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 card-shadow">
        <h2 className="font-heading text-base font-black text-slate-900 mb-3">Faturas</h2>
        {data.invoices.length === 0 ? (
          <p className="text-sm text-slate-500 italic" data-testid="card-empty">
            Nenhuma compra no cartão ainda. Lance em Financeiro → Despesas escolhendo "Cartão de crédito".
          </p>
        ) : (
          <ul className="space-y-2">
            {data.invoices.map((inv) => <InvoiceRow key={inv.month} inv={inv} onPay={setPayTarget} onUndo={undo} />)}
          </ul>
        )}
      </section>

      <PayDialog target={payTarget} onClose={() => setPayTarget(null)} onDone={() => { setPayTarget(null); load(); }} />
    </div>
  );
}
